import { t } from "i18next";
import { now } from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { BlockButton } from "../../components/BlockButton/BlockButton";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { Info } from "../../components/Info/Info";
import { SubMenu } from "../../components/SubMenu/SubMenu";
import { LayersIcon } from "../../icons/LayersIcon/LayersIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { go } from "../../services/NavigationService";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/create-floor.module.css";
import { PropertyArea } from "../../types/property";

type Props = {
  creationType: string;
};

export const CreatePropertyArea = ({ creationType }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [fileError, setFileError] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [propertyArea, setPropertyArea] = useState<PropertyArea>();
  const [propertyAreaIsCreated, setPropertyAreaIsCreated] = useState(false);

  const [map, setMap] = useState<File>();
  const [image, setImage] = useState("");
  const [mapIsUploaded, setIsMapUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const appointment = useSelector((state: RootState) => state.appointment);

  const isEditMode = location.state?.propertyAreaId !== undefined;
  const propertyAreaId = location.state?.propertyAreaId as string | undefined;

  useEffect(() => {
    dispatch(activeTabsActions.setDashbaordUrl(location.pathname));
    if (isEditMode && propertyAreaId) {
      PropertyService.GetPropertyArea(propertyAreaId)
        .then((response) => {
          const foundPropertyArea = response.data;
          setPropertyArea(foundPropertyArea);
          setInputValue(foundPropertyArea.label);
          setPropertyAreaIsCreated(true);
          if (foundPropertyArea.map && foundPropertyArea.map.url) {
            setImage(foundPropertyArea.map.url);
            setIsMapUploaded(true);
          }
        })
        .catch((error) => {
          console.log("Error fetching property area:", error);
          setError("Fehler beim Laden des Bereichs");
        });
    }
  }, [appointment, dispatch, location.pathname, isEditMode, propertyAreaId]);

  const handleInputChange = (customInput: string) => {
    setShowError(false);
    setInputValue(customInput);
  };

  const handleMapAdd = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Handle the selected image(s) here
      const selectedImage = files[0];
      const allowedTypes = ["image/jpeg", "image/png"];

      if (!allowedTypes.includes(selectedImage.type)) {
        setFileError("Nur JPG, PNG und SVG Dateien sind erlaubt.");
        setMap(undefined);
        setImage("");
        setIsMapUploaded(false);
        return;
      }

      setFileError("");
      setMap(selectedImage);
      setImage(URL.createObjectURL(selectedImage));
      setIsMapUploaded(true);
    }
  };
  const handleFloorCreation = async () => {
    setRequestLoading(true);

    try {
      if (isEditMode && propertyArea) {
        let mapUuid: string | null = propertyArea.map?.mediaUuid ?? null;

        if (map) {
          const formData = new FormData();
          formData.append("uuid", generateUUIDFromID(now().toString()));
          formData.append("name", map.name);
          formData.append("file", map);

          const uploadedImage = (await BackEndService.UploadImage(
            formData
          )) as any;
          if (!uploadedImage || !("uuid" in uploadedImage)) {
            throw new Error("Upload failed, no UUID returned");
          }
          mapUuid = uploadedImage.uuid;
        }

        await PropertyService.UpdatePropertyArea(propertyArea.id, {
          label: inputValue,
          map: mapUuid,
        });

        handleBackButtonClicked();
        return;
      }

      if (inputValue && !propertyAreaIsCreated) {
        let mapUuid: string | null = null;

        if (map) {
          const formData = new FormData();
          formData.append("uuid", generateUUIDFromID(now().toString()));
          formData.append("name", map.name);
          formData.append("file", map);

          const uploadedImage = (await BackEndService.UploadImage(
            formData
          )) as any;
          if (!uploadedImage || !("uuid" in uploadedImage)) {
            throw new Error("Upload failed, no UUID returned");
          }
          mapUuid = uploadedImage.uuid;
        }

        const propertyAreaRequestPayload = {
          propertyId: appointment.propertyId,
          label: inputValue,
          map: mapUuid,
        };

        PropertyService.CreatePropertyArea(propertyAreaRequestPayload)
          .then((propertyArea) => {
            setPropertyArea(propertyArea.data);
            setPropertyAreaIsCreated(true);
            handleBackButtonClicked();
          })
          .catch(() => {
            setShowError(true);
            setRequestLoading(false);
          });
      }
    } catch (error) {
      setError("Error saving property area");
    } finally {
      setRequestLoading(false);
    }
  };

  const handleBackButtonClicked = () => {
    if (isEditMode && propertyArea) {
      go(
        navigate,
        `/properties/${propertyArea.propertyId}/viewonly`,
        location.state
      );
      return;
    }
    if (creationType === "monitor") {
      go(navigate, "/monitors/create", location.state);
    } else if (creationType === "deficiency") {
      dispatch(activeTabsActions.setDeficienciesStep(2));

      go(navigate, "/deficiencies/create/step-2", location.state);
    }
  };

  const isButtonDisabled = inputValue.length === 0;

  return (
    <AppLayout hideHeader={true} className={styles["create-floor"]}>
      <div>
        <BackButtonHeader handleBackButtonClicked={handleBackButtonClicked} />

        <div className={styles["content-wrapper"]}>
          <AppointmentDetails />

          <div className={styles["content"]}>
            <SubMenu showSearchButton={false}>
              <span>{isEditMode ? "Ebene bearbeiten" : "Ebene erstellen"}</span>
              <LayersIcon color="dark" />
            </SubMenu>

            <CustomInput
              label={t("SelectEnterFloorName")}
              placeholder={
                propertyArea?.label ? propertyArea?.label : t("Enter")
              }
              onInputChangeLabel={t("EnterFloorName")}
              onInputChange={handleInputChange}
            />
            {error && <Info>{error.split(". ")[0]}</Info>}

            {propertyAreaIsCreated && !isEditMode && (
              <Info type="success">Bereich erfolgreich erstellt</Info>
            )}

            {showError && (
              <Info type="error">
                Ein Stockwerk mit diesem Namen existiert bereits.
              </Info>
            )}

            {(!mapIsUploaded || isEditMode) && (
              <BlockButton
                leftIcon="plus"
                rightIcon="map"
                onClick={handleMapAdd}
                className={
                  isEditMode ? styles["block-button--edit"] : undefined
                }
              >
                Karte hinzufügen
              </BlockButton>
            )}

            {image && (
              <div className={styles["map-wrapper"]}>
                <img
                  className={
                    isEditMode
                      ? styles["content__map-edit"]
                      : styles["content__map"]
                  }
                  src={image}
                  alt="Map"
                />
              </div>
            )}

            <input
              type="file"
              accept="image/*,android/force-camera-workaround"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileInputChange}
            />

            {fileError && <Info type="error">{fileError}</Info>}
          </div>
        </div>

        <FloatingButtons>
          <CustomButton
            color="white"
            icon="layers"
            loading={requestLoading}
            onClick={handleFloorCreation}
            disabled={isButtonDisabled}
            width={60}
          >
            Ebene eintragen
          </CustomButton>
        </FloatingButtons>
      </div>
    </AppLayout>
  );
};
